import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Button, 
  Descriptions, 
  Tag, 
  Spin, 
  message,
  Table,
  Space,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Row,
  Col,
  InputNumber,
  Skeleton,
  Tabs,
  List,
} from 'antd';
import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  DeleteOutlined,
  UserAddOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { Lead, Activity } from '../../types/leads';
import { getLeadById, updateLeadContacts, addOrUpdateOpportunity, removeOpportunity } from '../../api/leads';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { API_STALE_TIME_MS } from '../../constants/api';
import ContactEditModal from '../../components/modal/ContactEditModal';
import ConfirmDeleteModal from '../../components/modal/ConfirmDeleteModal';
import CurrencyInput from '../../components/common/CurrencyInput';

const { Title, Text } = Typography;
const { Option } = Select;

const OpportunityDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('sales');
  const [activityModalVisible, setActivityModalVisible] = useState(false);
  const [editingContact, setEditingContact] = useState<Lead['contacts'][number] | null>(null);
  const [contactForm] = Form.useForm();
  const [activityForm] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [openContactModal, setOpenContactModal] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [openDeleteActivityDialog, setOpenDeleteActivityDialog] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<Activity | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Lead['contacts'][number] | null>(null);
  const [openDeleteContactModal, setOpenDeleteContactModal] = useState(false);

  // Fetch the lead/opportunity using React Query and navigation state
  const {
    data: lead,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => getLeadById(id!),
    enabled: !!id,
    initialData: location.state?.lead,
    staleTime: API_STALE_TIME_MS,
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isModalVisible && lead) {
      contactForm.resetFields();
      contactForm.setFieldsValue({
        stage: lead.opportunity?.stage,
        units: lead.opportunity?.units,
        value: lead.opportunity?.value,
        expected_close_date: lead.opportunity?.expected_close_date ? dayjs(lead.opportunity.expected_close_date) : undefined,
      });
    }
  }, [isModalVisible, lead, contactForm]);

  // Reset activity form when modal opens/closes
  useEffect(() => {
    if (activityModalVisible) {
      activityForm.resetFields();
      if (editingActivity) {
        activityForm.setFieldsValue({
          type: editingActivity.type,
          date: dayjs(editingActivity.date),
          description: editingActivity.summary,
        });
      }
    }
  }, [activityModalVisible, editingActivity, activityForm]);

  const queryClient = useQueryClient();
  const updateContactsMutation = useMutation({
    mutationFn: ({ id, contacts }: { id: string, contacts: Lead['contacts'] }) => updateLeadContacts(id, contacts),
    onSuccess: () => {
      message.success(t('success:saveSuccess', { entity: t('contacts:contact') }));
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      setOpenContactModal(false);
      setEditingContact(null);
    },
    onError: (error) => {
      console.error('Failed to update contacts:', error);
      message.error(t('sales:errors.saveFailed', { entity: t('contacts:contact') }));
    }
  });

  const addOrUpdateOpportunityMutation = useMutation({
    mutationFn: ({ leadId, opportunity }: { leadId: string, opportunity: any }) => addOrUpdateOpportunity(leadId, opportunity),
    onSuccess: () => {
      message.success(t('success:saveSuccess', { entity: t('common.activity') }));
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      setActivityModalVisible(false);
      setEditingActivity(null);
    },
    onError: (error) => {
      console.error('Failed to update activities:', error);
      message.error(t('sales:errors.saveFailed', { entity: t('common.activity') }));
    }
  });

  const removeOpportunityMutation = useMutation({
    mutationFn: (leadId: string) => removeOpportunity(leadId),
    onSuccess: () => {
      message.success(t('success:deleteSuccess', { entity: t('opportunities.opportunity') }));
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      navigate('/sales/opportunities');
    },
    onError: () => {
      message.error(t('errors.deleteFailed', { entity: t('opportunities.opportunity') }));
    },
  });

  if (isLoading) {
    return <div style={{ padding: 32 }}><Card><Skeleton active /></Card></div>;
  }
  if (isError || !lead) {
    return <div style={{ padding: 32 }}><Card><Typography.Text type="danger">{t('errors:notFound', { entity: t('opportunities.opportunity') })}</Typography.Text></Card></div>;
  }

  const handleBack = () => {
    navigate('/sales/opportunities');
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setOpenContactModal(true);
  };

  const handleEditContact = (contact: Lead['contacts'][number]) => {
    setEditingContact(contact);
    setOpenContactModal(true);
  };

  const handleSaveContact = (newContacts: Lead['contacts']) => {
    if (!lead) return;
    let updatedContacts: Lead['contacts'];
    if (editingContact) {
      updatedContacts = lead.contacts.map((contact: Lead['contacts'][number]) =>
        contact.email === editingContact.email ? { ...newContacts[0], email: contact.email } : contact
      );
    } else {
      updatedContacts = [...lead.contacts, ...newContacts];
    }
    updateContactsMutation.mutate({ id: lead.id, contacts: updatedContacts });
  };

  const handleAddActivity = () => {
    setEditingActivity(null);
    activityForm.resetFields();
    setActivityModalVisible(true);
  };

  const handleEditActivity = (activity: Activity) => {
    setEditingActivity(activity);
    activityForm.setFieldsValue({
      type: activity.type,
      date: dayjs(activity.date),
      description: activity.summary,
    });
    setActivityModalVisible(true);
  };

  const handleDeleteActivity = (activity: Activity) => {
    setActivityToDelete(activity);
    setOpenDeleteActivityDialog(true);
  };

  const handleConfirmDeleteActivity = async () => {
    if (!lead || !lead.opportunity || !activityToDelete) return;
    const updatedActivities = (lead.opportunity.activities || []).filter((a: Activity) => a.id !== activityToDelete.id);
    await addOrUpdateOpportunityMutation.mutateAsync({
      leadId: lead.id,
      opportunity: { ...lead.opportunity, activities: updatedActivities },
    });
    setOpenDeleteActivityDialog(false);
    setActivityToDelete(null);
  };

  const handleActivitySave = async () => {
    try {
      const values = await activityForm.validateFields();
      if (!lead || !lead.opportunity) return;
      let activities = lead.opportunity.activities ? [...lead.opportunity.activities] : [];
      if (editingActivity) {
        // Edit
        activities = activities.map((a: Activity) =>
          a.id === editingActivity.id
            ? {
                ...a,
                type: values.type,
                summary: values.description,
                date: values.date.toDate(),
                updated_at: new Date(),
              }
            : a
        );
      } else {
        // Add
        activities.push({
          id: Date.now().toString(),
          type: values.type,
          summary: values.description,
          date: values.date.toDate(),
          performed_by: 'Current User',
          outcome: 'Completed',
          created_at: new Date(),
        });
      }
      await addOrUpdateOpportunityMutation.mutateAsync({
        leadId: lead.id,
        opportunity: { ...lead.opportunity, activities },
      });
    } catch (error) {
      console.error('Failed:', error);
    }
  };

  const handleEdit = () => {
    setIsModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await contactForm.validateFields();
      if (!lead) return;
      await addOrUpdateOpportunityMutation.mutateAsync({
        leadId: lead.id,
        opportunity: {
          ...lead.opportunity,
          stage: values.stage,
          units: values.units,
          value: values.value,
          expected_close_date: values.expected_close_date.toDate(),
        },
      });
      setIsModalVisible(false);
    } catch (error) {
      console.error('Failed to save opportunity:', error);
    }
  };

  const handleDelete = () => {
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (lead?.id) {
      removeOpportunityMutation.mutate(lead.id);
    }
  };

  const handleDeleteContact = (contact: Lead['contacts'][number]) => {
    setContactToDelete(contact);
    setOpenDeleteContactModal(true);
  };

  const confirmDeleteContact = async () => {
    if (!contactToDelete || !lead) return;
    const newContacts = lead.contacts.filter((c: Lead['contacts'][number]) => c !== contactToDelete);
    try {
      await updateContactsMutation.mutateAsync({ id: lead.id, contacts: newContacts });
      message.success(t('sales:success.updateSuccess', { entity: t('common:contacts') }));
      queryClient.invalidateQueries({ queryKey: ['lead', lead.id] });
    } catch (e) {
      message.error(t('sales:errors.updateFailed', { entity: t('common:contacts') }));
    }
    setOpenDeleteContactModal(false);
    setContactToDelete(null);
  };

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (!lead) {
    return <Text>{t('common:notFound')}</Text>;
  }

  const activityColumns = [
    {
      title: t('common:type', 'Type'),
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: t('common:date', 'Date'),
      dataIndex: 'date',
      key: 'date',
      render: (date: Date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: t('common:summary', 'Summary'),
      dataIndex: 'summary',
      key: 'summary',
    },
    {
      title: t('common:performedBy', 'Performed By'),
      dataIndex: 'performed_by',
      key: 'performed_by',
    },
    {
      title: t('common:outcome', 'Outcome'),
      dataIndex: 'outcome',
      key: 'outcome',
      render: (outcome: string) => (
        <Tag color={outcome === 'Completed' ? 'green' : 'blue'}>
          {outcome}
        </Tag>
      ),
    },
    {
      title: t('common:actions', 'Actions'),
      key: 'actions',
      render: (_: any, activity: Activity) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditActivity(activity)}>
            {t('common:edit', 'Edit')}
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteActivity(activity)}>
            {t('common:delete', 'Delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const contactTypeOptions = [
    { value: 'primary', label: 'Primary' },
    { value: 'secondary', label: 'Secondary' },
    { value: 'billing', label: 'Billing' },
    { value: 'technical', label: 'Technical' }
  ];

  const tabItems = [
    {
      key: '1',
      label: t('sales:opportunities.details.title'),
      children: (
        <Card style={{ marginBottom: 24 }}>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={t('sales:properties.table.name')}>{lead.name}</Descriptions.Item>
            <Descriptions.Item label={t('sales:opportunities.table.stage')}>
              {lead.opportunity?.stage ? (
                <Tag color="blue">
                  {t(`sales:opportunities.stages.${lead.opportunity.stage.toLowerCase()}`)}
                </Tag>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('sales:opportunities.table.units')}>{lead.opportunity?.units ?? '-'}</Descriptions.Item>
            <Descriptions.Item label={t('sales:opportunities.table.value')}>
              {lead.opportunity?.value ? new Intl.NumberFormat(t('common:locale', 'en-US'), {
                style: 'currency',
                currency: lead.opportunity.value.currency || 'USD',
              }).format(lead.opportunity.value.amount ?? 0) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('sales:opportunities.table.expected_close_date')}>
              {lead.opportunity?.expected_close_date ? dayjs(lead.opportunity.expected_close_date).format('YYYY-MM-DD') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('common:createdAt')}>{dayjs(lead.created_at).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
            <Descriptions.Item label={t('common:updatedAt')}>{dayjs(lead.updated_at).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: '2',
      label: t('sales:opportunities.details.activities'),
      children: (
        <Card
          title={t('sales:opportunities.details.activities', 'Activities')}
          style={{ marginBottom: 24 }}
          extra={
            <Button type="primary" icon={<CalendarOutlined />} onClick={handleAddActivity}>
              {t('common:addActivity', 'Add Activity')}
            </Button>
          }
        >
          <Table
            columns={activityColumns}
            dataSource={lead.opportunity?.activities || []}
            rowKey="id"
            pagination={false}
          />
        </Card>
      ),
    },
    {
      key: '3',
      label: t('sales:opportunities.details.contacts'),
      children: (
        <Card
          title={t('sales:opportunities.details.contacts', 'Contacts')}
          style={{ marginBottom: 24 }}
          extra={
            <Button type="primary" icon={<UserAddOutlined />} onClick={handleAddContact}>
              {t('common:addContact', 'Add Contact')}
            </Button>
          }
        >
          <List
            dataSource={lead.contacts}
            locale={{ emptyText: t('common:noData') }}
            renderItem={(contact: Lead['contacts'][number]) => (
              <List.Item
                actions={[
                  <Button type="link" onClick={() => handleEditContact(contact)}>{t('common:edit')}</Button>,
                  <Button type="link" danger onClick={() => handleDeleteContact(contact)}>{t('common:delete')}</Button>
                ]}
              >
                <List.Item.Meta
                  title={contact.name}
                  description={
                    <Space direction="vertical" size={0}>
                      <Typography.Text type="secondary">{contact.position}</Typography.Text>
                      <Typography.Text type="secondary">{contact.email}</Typography.Text>
                      <Typography.Text type="secondary">{contact.phone}</Typography.Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space align="center">
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} />
            <Title level={3} style={{ margin: 0 }}>{lead.name}</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>{t('common:edit')}</Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>{t('common:delete')}</Button>
          </Space>
        </Col>
      </Row>
      <Tabs items={tabItems} activeKey={activeTab} onChange={setActiveTab} />
      <ContactEditModal
        open={openContactModal}
        onSave={handleSaveContact}
        onCancel={() => {
          setOpenContactModal(false);
          setEditingContact(null);
        }}
        contact={editingContact || undefined}
        contactTypeOptions={contactTypeOptions}
        t={t}
        loading={updateContactsMutation.isPending}
        disabled={updateContactsMutation.isPending}
        form={contactForm}
      />
      <Modal
        title={editingActivity ? t('common:editActivity', 'Edit Activity') : t('common:addActivity', 'Add Activity')}
        open={activityModalVisible}
        onOk={handleActivitySave}
        onCancel={() => {
          setActivityModalVisible(false);
          setEditingActivity(null);
        }}
        destroyOnHidden
      >
        <Form
          form={activityForm}
          layout="vertical"
        >
          <Form.Item
            name="type"
            label={t('common:type', 'Type')}
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="Call">{t('sales:activities.types.call', 'Call')}</Option>
              <Option value="Meeting">{t('sales:activities.types.meeting', 'Meeting')}</Option>
              <Option value="Email">{t('sales:activities.types.email', 'Email')}</Option>
              <Option value="Note">{t('sales:activities.types.note', 'Note')}</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="date"
            label={t('common:date', 'Date')}
            rules={[{ required: true }]}
          >
            <DatePicker showTime />
          </Form.Item>
          <Form.Item
            name="description"
            label={t('common:description', 'Description')}
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={t('sales:opportunities.edit', 'Edit Opportunity')}
        open={isModalVisible}
        onOk={handleSave}
        onCancel={() => setIsModalVisible(false)}
        width={700}
        destroyOnHidden
      >
        <Form
          form={contactForm}
          layout="vertical"
          initialValues={{
            stage: lead.opportunity?.stage,
            units: lead.opportunity?.units,
            value: lead.opportunity?.value,
            expected_close_date: lead.opportunity?.expected_close_date ? dayjs(lead.opportunity.expected_close_date) : undefined,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="stage"
                label={t('sales:opportunities.fields.stage')}
                rules={[{ required: true, message: t('common:validation.required') }]}
              >
                <Select>
                  <Option value="prospecting">{t('sales:opportunities.stages.prospecting')}</Option>
                  <Option value="qualification">{t('sales:opportunities.stages.qualification')}</Option>
                  <Option value="proposal">{t('sales:opportunities.stages.proposal')}</Option>
                  <Option value="negotiation">{t('sales:opportunities.stages.negotiation')}</Option>
                  <Option value="closed_won">{t('sales:opportunities.stages.closed_won')}</Option>
                  <Option value="closed_lost">{t('sales:opportunities.stages.closed_lost')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="units"
                label={t('sales:opportunities.fields.units')}
                rules={[{ required: true, message: t('common:validation.required') }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="value"
                label={t('sales:opportunities.fields.value')}
              >
                <CurrencyInput value={contactForm.getFieldValue('value')} onChange={val => contactForm.setFieldsValue({ value: val })} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="expected_close_date"
                label={t('sales:opportunities.fields.expectedCloseDate')}
                rules={[{ required: true, message: t('common:validation.required') }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        title={t('common:confirmDelete', 'Confirm Delete')}
        open={openDeleteActivityDialog}
        onOk={handleConfirmDeleteActivity}
        onCancel={() => {
          setOpenDeleteActivityDialog(false);
          setActivityToDelete(null);
        }}
        okText={t('common:delete', 'Delete')}
        cancelText={t('common:cancel', 'Cancel')}
        okButtonProps={{ danger: true }}
      >
        {t('sales:activities.deleteConfirm', 'Are you sure you want to delete this activity? This action cannot be undone.')}
      </Modal>
      <ConfirmDeleteModal
        open={openDeleteDialog}
        title={t('common:confirmDelete', 'Confirm Delete')}
        description={t('sales:opportunities.deleteConfirm', 'Are you sure you want to delete this opportunity? This action cannot be undone.')}
        onOk={handleConfirmDelete}
        onCancel={() => setOpenDeleteDialog(false)}
        okText={t('common:delete', 'Delete')}
        cancelText={t('common:cancel', 'Cancel')}
        okButtonProps={{ danger: true }}
      />
      {openDeleteContactModal && (
        <ConfirmDeleteModal
          open={openDeleteContactModal}
          onOk={confirmDeleteContact}
          onCancel={() => { setOpenDeleteContactModal(false); setContactToDelete(null); }}
          title={t('common:confirmDeleteTitle')}
          description={t('common:confirmDelete')}
          okText={t('common:delete')}
          cancelText={t('common:cancel')}
        />
      )}
    </div>
  );
};

export default OpportunityDetailsPage; 